import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import {
  Settings,
  Filter,
  Aperture,
  Droplets,
  Wand2,
  Sun,
  Contrast,
  Lightbulb,
  RefreshCw
} from 'lucide-react';

export type FilterType =
  | 'none'
  | 'grayscale'
  | 'sepia'
  | 'invert'
  | 'blur'
  | 'brightness'
  | 'contrast'
  | 'saturate'
  | 'hue-rotate';

interface FilterOption {
  id: FilterType;
  label: string;
  icon: React.ReactNode;
  hasIntensity?: boolean;
  defaultIntensity?: number;
  maxIntensity?: number;
  unit?: string;
  cssFunction?: string;
}

interface VideoFiltersProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  isLocalVideo?: boolean;
  onFilterChange?: (filter: FilterType, intensity: number, filterStyle: string) => void;
}

const FilterOptions: FilterOption[] = [
  { id: 'none', label: 'Normal', icon: <RefreshCw size={16}/> },
  {
    id: 'grayscale',
    label: 'Grayscale',
    icon: <Filter size={16}/>,
    hasIntensity: true,
    maxIntensity: 100,
    unit: '%',
    cssFunction: 'grayscale'
  },
  {
    id: 'sepia',
    label: 'Sepia',
    icon: <Aperture size={16}/>,
    hasIntensity: true,
    maxIntensity: 100,
    unit: '%',
    cssFunction: 'sepia'
  },
  {
    id: 'invert',
    label: 'Invert',
    icon: <Contrast size={16}/>,
    hasIntensity: true,
    maxIntensity: 100,
    unit: '%',
    cssFunction: 'invert'
  },
  {
    id: 'blur',
    label: 'Blur',
    icon: <Droplets size={16}/>,
    hasIntensity: true,
    maxIntensity: 10,
    unit: 'px',
    cssFunction: 'blur'
  },
  {
    id: 'brightness',
    label: 'Brightness',
    icon: <Sun size={16}/>,
    hasIntensity: true,
    defaultIntensity: 100,
    maxIntensity: 200,
    unit: '%',
    cssFunction: 'brightness'
  },
  {
    id: 'contrast',
    label: 'Contrast',
    icon: <Contrast size={16}/>,
    hasIntensity: true,
    defaultIntensity: 100,
    maxIntensity: 200,
    unit: '%',
    cssFunction: 'contrast'
  },
  {
    id: 'saturate',
    label: 'Saturation',
    icon: <Wand2 size={16}/>,
    hasIntensity: true,
    defaultIntensity: 100,
    maxIntensity: 200,
    unit: '%',
    cssFunction: 'saturate'
  },
  {
    id: 'hue-rotate',
    label: 'Hue',
    icon: <Lightbulb size={16}/>,
    hasIntensity: true,
    maxIntensity: 360,
    unit: 'deg',
    cssFunction: 'hue-rotate'
  }
];

// Define filter message type for data channel
interface FilterMessage {
  type: 'filter-change';
  filter: FilterType;
  intensity: number;
}

export function VideoFilters({ videoRef, canvasRef, isLocalVideo = false, onFilterChange }: VideoFiltersProps) {
  const [ activeFilter, setActiveFilter ] = useState<FilterType>('none');
  const [ intensity, setIntensity ] = useState<number>(100);
  const [ isOpen, setIsOpen ] = useState(false);

  // Find the current filter option
  const currentFilter = FilterOptions.find(filter => filter.id === activeFilter);

  // Calculate filter style declaratively
  const filterStyle = useMemo(() => {
    if (activeFilter === 'none') return '';

    const filter = FilterOptions.find(f => f.id === activeFilter);
    if (!filter?.cssFunction) return '';

    const currentIntensity = filter.hasIntensity ? intensity : filter.defaultIntensity || 100;
    return `${filter.cssFunction}(${currentIntensity}${filter.unit || ''})`;
  }, [activeFilter, intensity]);

  const handleFilterChange = (filterId: FilterType) => {
    const newFilter = FilterOptions.find(f => f.id === filterId);
    setActiveFilter(filterId);

    // Reset intensity to default or 100% when switching filters
    const newIntensity = newFilter?.hasIntensity ? (newFilter.defaultIntensity || 100) : 100;
    setIntensity(newIntensity);

    // Calculate new filter style
    let newFilterStyle = '';
    if (filterId !== 'none' && newFilter?.cssFunction) {
      newFilterStyle = `${newFilter.cssFunction}(${newIntensity}${newFilter.unit || ''})`;
    }

    // Notify parent component immediately
    if (onFilterChange && isLocalVideo) {
      onFilterChange(filterId, newIntensity, newFilterStyle);
    }
  };

  const handleIntensityChange = (newIntensity: number) => {
    setIntensity(newIntensity);

    // Calculate new filter style
    let newFilterStyle = '';
    if (activeFilter !== 'none') {
      const filter = FilterOptions.find(f => f.id === activeFilter);
      if (filter?.cssFunction) {
        newFilterStyle = `${filter.cssFunction}(${newIntensity}${filter.unit || ''})`;
      }
    }

    // Notify parent component immediately
    if (onFilterChange && isLocalVideo) {
      onFilterChange(activeFilter, newIntensity, newFilterStyle);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={`relative bg-[#1C2431] hover:bg-[#2D3748] text-white p-2 rounded-lg flex items-center justify-center transition-colors border border-slate-700 ${activeFilter !== 'none' ? 'ring-2 ring-purple-500' : ''}`}
        >
          {activeFilter === 'none' ? (
            <Filter size={16}/>
          ) : (
            currentFilter?.icon || <Filter size={16}/>
          )}
          <span className="sr-only">Video Filters</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="right"
        align="start"
        className="w-60 p-0 bg-[#1E293B] border border-slate-700 text-white shadow-lg"
      >
        <div className="p-3 border-b border-slate-700">
          <h3 className="font-medium flex items-center gap-2">
            <Settings size={14}/>
            {isLocalVideo ? 'My Video Filters' : 'Video Filters'}
          </h3>
        </div>

        <div className="p-3 grid grid-cols-3 gap-2">
          {FilterOptions.map((filter) => (
            <Button
              key={filter.id}
              size="sm"
              variant="outline"
              className={`flex flex-col items-center justify-center h-16 p-1 bg-[#131A26] hover:bg-[#1C2431] border-slate-700 ${activeFilter === filter.id ? 'ring-2 ring-purple-500' : ''}`}
              onClick={() => handleFilterChange(filter.id)}
            >
              {filter.icon}
              <span className="text-xs mt-1">{filter.label}</span>
            </Button>
          ))}
        </div>

        {currentFilter?.hasIntensity && (
          <div className="p-3 border-t border-slate-700">
            <Label className="flex items-center justify-between mb-2">
              <span>Intensity</span>
              <span className="text-xs text-gray-400">
                {intensity}{currentFilter.unit}
              </span>
            </Label>
            <Slider
              value={[ intensity ]}
              min={0}
              max={currentFilter.maxIntensity || 100}
              step={1}
              onValueChange={(values) => handleIntensityChange(values[0])}
              className="cursor-pointer"
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}